import { useState } from 'react';
import { Download, FileText, RefreshCw, Trash2, X } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FileUpload } from '@/components/ui/FileUpload';
import { FormSection } from '@/components/ui/FormSection';
import { DOCUMENT_KINDS, type DocumentKind } from '@/features/institute/admissions/constants';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { downloadPrivateFile } from '@/lib/download';
import { formatDate } from '@/lib/format';
import type { StudentDocument } from '@/types/student';

import { studentsApi, type StudentDocumentsState } from '../api/students.api';

export interface StudentDocumentsPanelProps {
  studentUuid: string;
  studentName: string;
  /** What the record carried when it loaded. The panel keeps its own copy after that. */
  initial: StudentDocumentsState;
  canManage: boolean;
}

/** `184320` → `180 KB`. */
function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function reasonFor(caught: unknown): string {
  return caught instanceof ApiRequestError
    ? caught.message
    : 'Something went wrong. Please try again.';
}

/**
 * A child's certificates on their record, one slot each.
 *
 * The admission form collects these at the counter, but a child brought in from
 * a spreadsheet arrives with none — a sheet cannot carry files. This is where
 * they are attached afterwards.
 *
 * Each slot saves on its own, the moment a file is chosen, and is independent
 * of the form's Save button: a certificate is bytes in storage, not a field in
 * the record, and an office scanning papers one by one should not lose them to a
 * validation error three sections up.
 *
 * "Pending" is the server's word, not ours — `missingDocuments` applies the
 * admission's own rule to the record as saved.
 */
export function StudentDocumentsPanel({
  studentUuid,
  studentName,
  initial,
  canManage,
}: StudentDocumentsPanelProps) {
  const [state, setState] = useState<StudentDocumentsState>(initial);
  /** The slot being uploaded to, with the file on its way. */
  const [uploading, setUploading] = useState<{ field: DocumentKind; file: File } | null>(null);
  /** Slots the office has asked to replace — they show the picker instead of the file. */
  const [replacing, setReplacing] = useState<DocumentKind | null>(null);
  const [removing, setRemoving] = useState<DocumentKind | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [downloading, setDownloading] = useState<DocumentKind | null>(null);

  const byField = new Map<DocumentKind, StudentDocument>(
    state.documents.map((document) => [document.field, document]),
  );
  const pending = new Set(state.missingDocuments);
  const isBusy = uploading !== null || isRemoving;

  async function upload(field: DocumentKind, file: File) {
    setUploading({ field, file });
    try {
      setState(await studentsApi.uploadDocument(studentUuid, field, file));
      setReplacing(null);
      toast.success(`${labelOf(field)} attached`, `It is on ${studentName}'s record now.`);
    } catch (caught) {
      toast.error(`Could not attach the ${labelOf(field).toLowerCase()}`, reasonFor(caught));
    } finally {
      setUploading(null);
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    const field = removing;
    setIsRemoving(true);
    try {
      setState(await studentsApi.removeDocument(studentUuid, field));
      toast.success(`${labelOf(field)} taken off the record`);
      setRemoving(null);
    } catch (caught) {
      toast.error('Could not take that document off', reasonFor(caught));
    } finally {
      setIsRemoving(false);
    }
  }

  async function view(field: DocumentKind, document: StudentDocument) {
    setDownloading(field);
    try {
      await downloadPrivateFile(
        studentsApi.documentPath(studentUuid, field),
        document.originalName,
      );
    } catch (caught) {
      toast.error('Could not open that document', reasonFor(caught));
    } finally {
      setDownloading(null);
    }
  }

  return (
    <FormSection
      title="H. Documents"
      description="Certificates on this student's record. Each one saves as soon as you choose the file — you do not need to press Save changes."
      columns={1}
    >
      {pending.size > 0 && (
        <Alert
          tone="warning"
          title={`${pending.size} ${pending.size === 1 ? 'document is' : 'documents are'} pending`}
        >
          This admission needs the papers marked Pending. Students added from a spreadsheet start
          with none on file.
        </Alert>
      )}

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {DOCUMENT_KINDS.map((kind) => {
          const document = byField.get(kind.key);
          const isPending = pending.has(kind.key);
          const isUploadingHere = uploading?.field === kind.key;
          const showPicker = canManage && (!document || replacing === kind.key);

          return (
            <li key={kind.key} className="rounded-field border border-line bg-surface p-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-[560] text-ink">{kind.label}</p>
                {document ? (
                  <Badge tone="success" withDot>
                    On file
                  </Badge>
                ) : isPending ? (
                  <Badge
                    tone="warning"
                    withDot
                    className="bg-amber-100 border border-amber-500 text-[rgb(230,71,95)] font-bold shadow-2xs dark:bg-amber-950/90 dark:border-amber-900/90"
                  >
                    Pending
                  </Badge>
                ) : (
                  <Badge>Optional</Badge>
                )}
              </div>

              {document && replacing !== kind.key && (
                <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <FileText className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
                    <p className="min-w-0 flex-1 text-sm text-ink-2">
                      <span className="block truncate font-medium text-ink" title={document.originalName}>
                        {document.originalName}
                      </span>
                      <span className="text-xs text-ink-3">
                        {formatSize(document.sizeBytes)} · added {formatDate(document.createdAt)}
                      </span>
                    </p>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      variant="secondary"
                      iconOnly
                      leftIcon={<Download className="h-4 w-4" aria-hidden />}
                      aria-label="Download"
                      title="Download"
                      isLoading={downloading === kind.key}
                      onClick={() => void view(kind.key, document)}
                    />
                    {canManage && (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          iconOnly
                          leftIcon={<RefreshCw className="h-4 w-4" aria-hidden />}
                          aria-label="Replace"
                          title="Replace"
                          disabled={isBusy}
                          onClick={() => setReplacing(kind.key)}
                        />
                        <Button
                          size="sm"
                          variant="dangerSoft"
                          iconOnly
                          leftIcon={<Trash2 className="h-4 w-4" aria-hidden />}
                          aria-label="Remove"
                          title="Remove"
                          disabled={isBusy}
                          onClick={() => setRemoving(kind.key)}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

              {showPicker && (
                <div className="mt-2 flex flex-col gap-2">
                  <FileUpload
                    label={document ? `New ${kind.label.toLowerCase()}` : kind.label}
                    hint={kind.hint}
                    required={isPending}
                    file={uploading?.field === kind.key ? uploading.file : null}
                    disabled={isBusy}
                    onSelect={(file) => {
                      if (file) void upload(kind.key, file);
                    }}
                  />
                  {isUploadingHere && (
                    <p className="text-xs text-ink-3" role="status">
                      Uploading…
                    </p>
                  )}
                  {replacing === kind.key && !isUploadingHere && (
                    <div>
                      <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<X className="h-3.5 w-3.5" />}
                        onClick={() => setReplacing(null)}
                      >
                        Keep the current file
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {!document && !canManage && <p className="mt-1 text-sm text-ink-3">Not on file.</p>}
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        isOpen={removing !== null}
        title={`Remove the ${removing ? labelOf(removing).toLowerCase() : 'document'}?`}
        description="It stays in the record history but is no longer shown here. If this admission needs it, the student shows as having documents pending until a new one is attached."
        confirmLabel="Remove document"
        tone="danger"
        isLoading={isRemoving}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </FormSection>
  );
}

function labelOf(field: DocumentKind): string {
  return DOCUMENT_KINDS.find((kind) => kind.key === field)?.label ?? 'Document';
}
